import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, FileText, TrendingUp, Brain, Dumbbell, 
  Sparkles, Calendar, Target, Zap, ChevronRight, Download
} from "lucide-react";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar
} from "recharts";
import { PremiumPageGuard } from "../components/PremiumPageGuard";
import { devError } from "../utils/devLogger";

const WeeklyReport = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [reportRes, correlationsRes, recsRes] = await Promise.all([
        axios.get(`${API}/reports/weekly`),
        axios.get(`${API}/insights/correlations`),
        axios.get(`${API}/recommendations`)
      ]);
      setReport(reportRes.data);
      setCorrelations(correlationsRes.data);
      setRecommendations(recsRes.data.recommendations);
    } catch (error) {
      devError("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getActionIcon = (action) => {
    switch(action) {
      case 'training': return Dumbbell;
      case 'meditation': return Brain;
      case 'games': return Sparkles;
      case 'calendar': return Calendar;
      default: return Target;
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-sky-100 text-sky-700 border-sky-200';
    }
  };

  const exportPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    
    doc.setFontSize(22);
    doc.setTextColor(14, 116, 144);
    doc.text("LumiFit Weekly Report", 20, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`${report?.period?.start || ''} - ${report?.period?.end || ''}`, 20, 35);
    
    let y = 50;
    
    // Wellness Averages
    doc.setFontSize(14);
    doc.setTextColor(14, 116, 144);
    doc.text("Wellness Overview", 20, y);
    y += 10;
    
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    const metrics = [
      `Mood: ${report?.averages?.mood || 0}/10`,
      `Energy: ${report?.averages?.energy || 0}/10`,
      `Focus: ${report?.averages?.focus || 0}/10`,
      `Sleep: ${report?.averages?.sleep || 0}/10`,
      `Stress: ${report?.averages?.stress || 0}/10`
    ];
    metrics.forEach(m => { doc.text(m, 25, y); y += 7; });
    
    y += 5;
    doc.setFontSize(14);
    doc.setTextColor(14, 116, 144);
    doc.text("Exercise Summary", 20, y);
    y += 10;
    
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text(`Physical Completed: ${report?.exercises?.physical_completed || 0}`, 25, y); y += 7;
    doc.text(`Cognitive Completed: ${report?.exercises?.cognitive_completed || 0}`, 25, y); y += 7;
    doc.text(`Days Tracked: ${report?.days_tracked || 0}/7`, 25, y); y += 7;
    doc.text(`Current Streak: ${report?.streak?.current || 0} days`, 25, y); y += 12;
    
    if (report?.ai_insight) {
      doc.setFontSize(14);
      doc.setTextColor(14, 116, 144);
      doc.text("AI Weekly Summary", 20, y);
      y += 10;
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      const lines = doc.splitTextToSize(report.ai_insight, 170);
      doc.text(lines, 25, y);
      y += lines.length * 5 + 10;
    }
    
    if (report?.correlations?.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(14, 116, 144);
      doc.text("Key Insights", 20, y);
      y += 10;
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      report.correlations.forEach(c => {
        const lines = doc.splitTextToSize(`- ${c}`, 170);
        doc.text(lines, 25, y);
        y += lines.length * 5 + 3;
      });
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text("Generated by LumiFit - Body & Mind Training", 20, 285);
    
    doc.save(`LumiFit_Weekly_Report_${report?.period?.start || 'report'}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  const radarData = report ? [
    { metric: 'Mood', value: report.averages.mood },
    { metric: 'Energy', value: report.averages.energy },
    { metric: 'Focus', value: report.averages.focus },
    { metric: 'Sleep', value: report.averages.sleep },
    { metric: 'Stress', value: report.averages.stress }
  ] : [];

  return (
    <div className="min-h-screen bg-sky-50" data-testid="weekly-report-page">
      <div className="fixed top-0 left-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />
      
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={exportPDF}
                className="text-sky-600 hover:text-sky-800"
                data-testid="export-pdf-btn"
                disabled={!report}
              >
                <Download className="w-5 h-5 mr-2" strokeWidth={1.5} />
                PDF
              </Button>
              <FileText className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('report.weeklyReport')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-2">
              {report?.period?.start} - {report?.period?.end}
            </p>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
              {t('report.title')}
            </h1>
          </div>

          {recommendations.length > 0 && (
            <div className="mb-8 space-y-3">
              {recommendations.map((rec) => {
                const Icon = getActionIcon(rec.action);
                return (
                  <div
                    key={`rec-${rec.title}`}
                    className={`glass-card p-4 border-l-4 ${getPriorityColor(rec.priority)}`}
                    data-testid={`recommendation-${rec.title}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sky-800">{rec.title}</h4>
                        <p className="text-sm text-slate-600">{rec.message}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {report?.ai_insight && (
            <div className="glass-card p-6 mb-8 border-l-4 border-sky-400 glow-primary" data-testid="ai-insight">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-400/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-sky-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-sky-800 mb-2">{t('report.aiWeeklySummary')}</h3>
                  <p className="text-slate-600 leading-relaxed">{report.ai_insight}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass-card p-6" data-testid="wellness-radar">
              <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                {t('report.wellnessOverview')}
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e0f2fe" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Radar name="This Week" dataKey="value" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.3} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass-card p-6" data-testid="exercise-stats">
              <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                {t('report.exerciseSummary')}
              </h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Dumbbell className="w-5 h-5 text-cyan-500" />
                      <span className="text-slate-600">{t('training.summary.physical')}</span>
                    </div>
                    <span className="font-medium text-sky-800">{report?.exercises?.physical_completed || 0}</span>
                  </div>
                  <Progress value={Math.min((report?.exercises?.physical_completed || 0) / 20 * 100, 100)} className="h-2 bg-cyan-100" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-indigo-500" />
                      <span className="text-slate-600">{t('training.summary.cognitive')}</span>
                    </div>
                    <span className="font-medium text-sky-800">{report?.exercises?.cognitive_completed || 0}</span>
                  </div>
                  <Progress value={Math.min((report?.exercises?.cognitive_completed || 0) / 20 * 100, 100)} className="h-2 bg-indigo-100" />
                </div>
                <div className="pt-4 border-t border-sky-100">
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t('report.daysTracked')}</span>
                    <span className="font-medium text-sky-800">{report?.days_tracked || 0} / 7</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('achievements.currentStreak')}</span>
                  <span className="font-medium text-amber-500">{report?.streak?.current || 0} {t('common.days')}</span>
                </div>
              </div>
            </div>

            {correlations?.data?.length > 0 && (
              <div className="glass-card p-6 lg:col-span-2" data-testid="correlations-chart">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                  {t('report.bodyMindTrends')}
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={correlations.data.slice(-14)}>
                      <defs>
                        <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="focusGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#a5b4fc" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#a5b4fc" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                      <XAxis 
                        dataKey="date" stroke="#64748b" fontSize={11}
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis domain={[0, 10]} stroke="#64748b" fontSize={11} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255,255,255,0.95)', 
                          border: 'none', borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Area type="monotone" dataKey="energy" stroke="#22d3ee" fill="url(#energyGrad)" strokeWidth={2} name="Energy" />
                      <Area type="monotone" dataKey="focus" stroke="#a5b4fc" fill="url(#focusGrad)" strokeWidth={2} name="Focus" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {correlations?.insights?.length > 0 && (
              <div className="glass-card p-6 lg:col-span-2" data-testid="insights">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                  <TrendingUp className="w-5 h-5 inline mr-2 text-green-500" />
                  {t('report.discoveredPatterns')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {correlations.insights.map((insight) => (
                    <div key={`insight-${insight.title}`} className="p-4 bg-white/50 rounded-xl border border-green-100">
                      <h4 className="font-medium text-sky-800 mb-1">{insight.title}</h4>
                      <p className="text-sm text-slate-600">{insight.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report?.correlations?.length > 0 && (
              <div className="glass-card p-6 lg:col-span-2">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                  <Zap className="w-5 h-5 inline mr-2 text-amber-500" />
                  {t('report.quickInsights')}
                </h3>
                <div className="space-y-2">
                  {report.correlations.map((corr, idx) => (
                    <p key={`corr-${idx}-${corr.slice(0,20)}`} className="text-slate-600 flex items-start gap-2">
                      <span className="text-amber-500">&bull;</span>
                      {corr}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const WeeklyReportGuarded = () => (
  <PremiumPageGuard feature="Weekly AI Reports">
    <WeeklyReport />
  </PremiumPageGuard>
);

export default WeeklyReportGuarded;
